import { Component, DestroyRef, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, concatMap, EMPTY, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { formatFromDate, formatFromDateWithTimezone } from 'src/app/common/datetime_utility';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {
  IntegrationCreatedComponent,
  IntegrationCreatedInput,
} from 'src/app/components/integration-created/integration-created.component';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { Label, Labels } from 'src/app/labels/englishLabels';
import { IntegrationStatusModel } from 'src/app/models/api';
import {
  CardActionClicked,
  CombinedIntegration,
  getIntegrationStatusLabel,
  IInfoCard,
  IntegrationActionsEnum,
  IntegrationsActionsNamesToLabelsMap,
  sortIntegrationsByStatus,
  StatusCodeEnum,
  StatusReasonEnum,
} from 'src/app/models/api/integration.model';
import { IIntegrationComponentInput } from 'src/app/models/componentInputs';
import { IIntegrationComponentOutput } from 'src/app/models/componentInputs/integrationComponentInput.interface';
import { PlatformIconPaths } from 'src/app/models/constants/platform.constants';
import {
  PopStartingHeight,
  PopUpMaxHeight,
  PopUpStyling,
  PopUpWidth,
  PopUpWidthSmall,
} from 'src/app/models/constants/popup.constants';
import { MenuAction } from 'src/app/models/menuAction.model';
import { InternalNavigationTriggerModel } from 'src/app/models/navigation/internalNavigationTrigger.model';
import { NavigationParams } from 'src/app/models/navigation/navigationParams.interface';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { IntegrationsHttpService } from 'src/app/services/httpServices/integrationsHttpsService/integrations-http.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { QueryParamsService } from 'src/app/services/queryParams/query-params.service';
import { IntegrationComponent } from '../integration/integration.component';
import { UserService } from 'src/app/services/infra/user/user.service';
import { HttpStatusCode } from '@angular/common/http';
import { getPlatformDisplayName } from 'src/app/common/platformUtils';
import { AuthenticationMethodToLabelPath } from 'src/app/models/constants/credentials.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { InLineIntegrationsHttpService } from 'src/app/services/httpServices/edgeIntegrationsHttpsService/in-line-integrations-http.service';
import { InLineIntegration } from 'src/app/models/api/inLineIntegration/inLineIntegration.model';

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
})
export class IntegrationsComponent implements OnInit, OnDestroy {
  public readonly numOfIntegrationsPerRow: number = 3;
  public integrationsCards$: Observable<IInfoCard[]> = of([]);

  public isInLineControlIntegrationFeatureEnabled: WritableSignal<boolean> = signal<boolean>(false);
  public isNewIntegrationLayoutEnabled: WritableSignal<boolean> = signal<boolean>(false);
  public inlineIntegrations: InLineIntegration[] = [];

  public status = StatusLabelEnum.active;

  // If you change max integrations number please change it in add integration api as well
  public maxIntegrations = 200;

  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public disabledAddIntegrationButtonReason = '';

  private readonly _queryParamsSubscription$: Subscription;
  private _lastNavigationParams: NavigationParams = {};

  private readonly _integrationsHttpService = inject(IntegrationsHttpService);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private readonly _storeService = inject(StoreService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _loggerService = inject(LoggerService);
  private readonly _inLineIntegrationsHttpService = inject(InLineIntegrationsHttpService);

  constructor(
    public permissionsService: PermissionsService,
    public userService: UserService,
    private readonly _dialogRef: MatDialog,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _queryParamsService: QueryParamsService
  ) {
    this._queryParamsSubscription$ = this._queryParamsService.toNavigate$.subscribe(
      (navigationParams: NavigationParams) => {
        if (navigationParams.integration) {
          if (!this._canEdit()) {
            this._removeIdFromUrl();
          } else {
            // do not trigger dialog open if same id
            if (navigationParams.integration != this._lastNavigationParams.integration) {
              this._openIntegrationDialog({
                integrationId: navigationParams.integration,
                parentIsLoading: this.isLoading$,
                isInAddMode: false,
              });
            }
            this._lastNavigationParams = navigationParams;
          }
        } else {
          this._lastNavigationParams = navigationParams;
        }
      }
    );
  }

  ngOnInit(): void {
    this.isInLineControlIntegrationFeatureEnabled.set(
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.InlinePrevention)
    );
    this.isNewIntegrationLayoutEnabled.set(
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.EnableNewIntegrationPageLayout)
    );
    this._initIntegrationsCards();
  }

  ngOnDestroy(): void {
    this._queryParamsSubscription$?.unsubscribe();
  }

  public actionOnClick(event: CardActionClicked) {
    this._handleIntegrationActionClicked(event);
  }

  public addIntegration(): void {
    this._openIntegrationDialog({
      isInAddMode: true,
      parentIsLoading: this.isLoading$,
    });
  }

  private _openIntegrationDialog(input: IIntegrationComponentInput): MatDialogRef<IntegrationComponent, boolean> {
    const dialogRef = this._dialogRef.open(IntegrationComponent, {
      height: PopStartingHeight,
      width: PopUpWidth,
      maxHeight: PopUpMaxHeight,
      minHeight: PopStartingHeight,
      data: input,
      panelClass: PopUpStyling,
    });

    dialogRef
      .afterClosed()
      .pipe(
        tap((output: IIntegrationComponentOutput) => {
          if (output.refreshNeeded) {
            this._updateIntegrationsCards();

            if (input.isInAddMode) {
              this._openNewIntegrationCreated(output.integrationId);
            }
          }

          this._removeIdFromUrl();
        })
      )
      .subscribe();

    return dialogRef;
  }

  private _openNewIntegrationCreated(integrationId: string) {
    this._dialogRef.open(IntegrationCreatedComponent, {
      height: '190px',
      width: PopUpWidthSmall,
      data: {
        integrationId: integrationId,
      } as IntegrationCreatedInput,
      panelClass: PopUpStyling,
    });
  }

  private _removeIdFromUrl() {
    this._router.navigate(['.'], {
      relativeTo: this._route,
      queryParams: { integration: null },
      queryParamsHandling: 'merge',
    });
  }

  private _handleIntegrationActionClicked(actionClicked: CardActionClicked): void {
    const card = actionClicked.card;
    const action = actionClicked.action.name;

    switch (action) {
      case IntegrationActionsEnum.enable.toString():
        this._handleEnableActionClicked(card);
        break;
      case IntegrationActionsEnum.disable.toString():
        this._handleDisableActionClicked(card);
        break;
      case IntegrationActionsEnum.edit.toString():
        // Dialog is opened via query param navigation (card pushes ?integration=<id>)
        // which triggers the _queryParamsSubscription$ subscriber
        break;
      case IntegrationActionsEnum.delete.toString():
        this._handleDeleteActionClicked(card);
        break;
      case IntegrationActionsEnum.retry.toString():
        this._handleRetryAuthActionClicked(card);
        break;
    }
  }

  private _handleEnableActionClicked(integrationCard: IInfoCard): void {
    this._integrationsHttpService
      .enableIntegration(integrationCard.cardId)
      .pipe(
        tap(() => this._updateIntegrationsCards()),
        catchError((error) => {
          this._httpErrorHandlerService.handle(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _handleDisableActionClicked(integrationCard: IInfoCard): void {
    this._integrationsHttpService
      .disableIntegration(integrationCard.cardId)
      .pipe(
        tap(() => this._updateIntegrationsCards()),
        catchError((error) => {
          this._httpErrorHandlerService.handle(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _handleDeleteActionClicked(integrationCard: IInfoCard): void {
    const dialogRef = this._dialogRef.open(ConfirmationDialogComponent, {
      data: {
        title: Labels.integrationsPage.deleteConfirmation.title,
        content: Labels.integrationsPage.deleteConfirmation.content,
      },
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(
        concatMap((userConfirmed: boolean) => {
          if (!userConfirmed) {
            return of(null);
          }

          const cachedInlineIntegration = this.inlineIntegrations.find(
            (inline) => inline.integrationId === integrationCard.cardId
          );

          if (cachedInlineIntegration) {
            return this._deleteInlineIntegrationThenMain(integrationCard.cardId, cachedInlineIntegration.id);
          }

          if (this.isInLineControlIntegrationFeatureEnabled()) {
            this._loggerService.info('Inline integration not in cache, fetching from server', {
              integrationId: integrationCard.cardId,
            });

            return this._inLineIntegrationsHttpService
              .getInLineIntegrationByRelatedIntegrationId(integrationCard.cardId)
              .pipe(
                switchMap((inlineIntegration) => {
                  if (inlineIntegration) {
                    return this._deleteInlineIntegrationThenMain(integrationCard.cardId, inlineIntegration.id);
                  }
                  return this._deleteMainIntegration(integrationCard.cardId);
                }),
                catchError((error) => {
                  if (error.status === 404) {
                    this._loggerService.info('No inline integration found, deleting main integration only', {
                      integrationId: integrationCard.cardId,
                    });
                  } else {
                    this._loggerService.error(
                      'Failed to fetch inline integration, proceeding with main integration deletion',
                      { integrationId: integrationCard.cardId },
                      error
                    );
                  }
                  return this._deleteMainIntegration(integrationCard.cardId);
                })
              );
          }

          return this._deleteMainIntegration(integrationCard.cardId);
        })
      )
      .subscribe();
  }

  private _deleteInlineIntegrationThenMain(integrationId: string, inlineIntegrationId: string): Observable<unknown> {
    this._loggerService.info('Deleting inline integration before main integration', {
      integrationId,
      inlineIntegrationId,
    });

    return this._inLineIntegrationsHttpService.deleteInLineIntegration(inlineIntegrationId).pipe(
      tap(() =>
        this._loggerService.info('Deleted inline integration successfully', {
          integrationId,
          inlineIntegrationId,
        })
      ),
      switchMap(() => this._integrationsHttpService.deleteIntegration(integrationId)),
      tap(() => this._updateIntegrationsCards()),
      catchError((error) => {
        this._httpErrorHandlerService.handle(error);
        return EMPTY;
      }),
      takeUntilDestroyed(this._destroyRef)
    );
  }

  private _deleteMainIntegration(integrationId: string): Observable<unknown> {
    return this._integrationsHttpService.deleteIntegration(integrationId).pipe(
      tap(() => this._updateIntegrationsCards()),
      catchError((error) => {
        this._httpErrorHandlerService.handle(error);
        return EMPTY;
      }),
      takeUntilDestroyed(this._destroyRef)
    );
  }

  private _handleRetryAuthActionClicked(integrationCard: IInfoCard): void {
    this._integrationsHttpService
      .retryIntegration(integrationCard.cardId)
      .pipe(
        tap(() => this._updateIntegrationsCards()),
        catchError((error) => {
          this._httpErrorHandlerService.handle(
            error,
            error.status === HttpStatusCode.UnprocessableEntity ? true : false
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _getActionsMenu(integrationStatus: IntegrationStatusModel) {
    const menuActions: MenuAction[] = [];
    const integrationActions = this._getIntegrationActions(integrationStatus);
    const disableEditIntegration = !this._canEdit();

    integrationActions.forEach((action) => {
      switch (action) {
        case IntegrationActionsEnum.disable.toString():
          menuActions.push({
            name: IntegrationActionsEnum.disable,
            displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.disable],
            disabled: disableEditIntegration,
          });
          break;
        case IntegrationActionsEnum.edit.toString():
          menuActions.push({
            name: IntegrationActionsEnum.edit,
            displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.edit],
            disabled: disableEditIntegration,
            shouldOpenDialog: true,
          });
          break;
        case IntegrationActionsEnum.enable.toString():
          menuActions.push({
            name: IntegrationActionsEnum.enable,
            displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.enable],
            disabled: disableEditIntegration,
          });
          break;
        case IntegrationActionsEnum.delete.toString():
          menuActions.push({
            name: IntegrationActionsEnum.delete,
            displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.delete],
            disabled: disableEditIntegration,
          });
          break;
        case IntegrationActionsEnum.retry.toString():
          menuActions.push({
            name: IntegrationActionsEnum.retry,
            displayNameLabel: IntegrationsActionsNamesToLabelsMap[IntegrationActionsEnum.retry],
            disabled: disableEditIntegration,
          });
          break;
      }
    });

    return menuActions;
  }

  private _initIntegrationsCards(): void {
    this._setIntegrationsCards(this._storeService.integrations$);
  }

  private _updateIntegrationsCards(): void {
    this._setIntegrationsCards(this._storeService.updateIntegrations$());
  }

  private _setIntegrationsCards(source$: Observable<CombinedIntegration[]>): void {
    this.isLoading$.next(true);
    this.integrationsCards$ = source$.pipe(
      switchMap((integrations) => {
        if (integrations.length === 0) {
          if (this._canEdit()) {
            this.addIntegration();
          }
          this.isLoading$.next(false);
          return EMPTY;
        }

        // Fetch inline integrations if feature flag is enabled
        if (this.isInLineControlIntegrationFeatureEnabled()) {
          return this._inLineIntegrationsHttpService.getAllInLineIntegrations().pipe(
            catchError((error) => {
              this._loggerService.error('Failed to fetch inline integrations', {}, error);
              return of([]);
            }),
            tap((inlineIntegrations) => {
              this.inlineIntegrations = inlineIntegrations;
            }),
            map(() => integrations)
          );
        }

        return of(integrations);
      }),
      map((combinedIntegrations) => this._createIntegrationCards(sortIntegrationsByStatus(combinedIntegrations))),
      tap((integrationCards) => {
        if (!this.permissionsService.isAdmin()) {
          this.disabledAddIntegrationButtonReason = this._labelsProviderPipe.transform(
            'integrationsPage.notAllowedToAddIntegrationMsg'
          );
        } else if (integrationCards.length >= this.maxIntegrations) {
          this.disabledAddIntegrationButtonReason = this._labelsProviderPipe.transform(
            'integrationsPage.maxIntegrationsMsg'
          );
        }
        this.isLoading$.next(false);
      }),
      catchError((error) => {
        this._loggerService.error('IntegrationsComponent: pipeline error', {}, error);
        this.isLoading$.next(false);
        return of([]);
      })
    );
  }

  private _createIntegrationCards(integrations: CombinedIntegration[]): IInfoCard[] {
    const cards: IInfoCard[] = [];

    integrations.map((integration) => {
      // Check if this integration has a matching inline integration
      // Only mark as having an inline webhook if the feature is enabled and we have a cached inline integration
      const hasInlineWebhook =
        this.isInLineControlIntegrationFeatureEnabled() &&
        this.inlineIntegrations.some((inlineIntegration) => inlineIntegration.integrationId === integration.id);

      // Build different fields based on feature flag
      const leftCardFields: ICardField[] = this.isNewIntegrationLayoutEnabled()
        ? [
            // New layout fields
            {
              key: this._labelsProviderPipe.transform('integrationsPage.platformField'),
              value: getPlatformDisplayName(integration.platform),
            },
            {
              key: 'Integration type',
              value: 'API Connection',
              isChipStyle: true,
              additionalValues: hasInlineWebhook ? ['In-Line webhook'] : undefined,
            },
            {
              key: this._labelsProviderPipe.transform('integrationsPage.environmentField'),
              value: this._beautifyDisplayStringPipe.transform(integration.environment),
            },
            {
              key: this._labelsProviderPipe.transform('integrationsPage.integrationMethodField'),
              value: Label.get(AuthenticationMethodToLabelPath[integration.authenticationMethod]),
            },
          ]
        : [
            // Legacy layout fields (with platform icon and integration ID)
            {
              key: this._labelsProviderPipe.transform('integrationsPage.platformField'),
              value: getPlatformDisplayName(integration.platform),
              iconPath: PlatformIconPaths[integration.platform],
            },
            {
              key: this._labelsProviderPipe.transform('integrationsPage.environmentField'),
              value: this._beautifyDisplayStringPipe.transform(integration.environment),
            },
            {
              key: this._labelsProviderPipe.transform('integrationsPage.integrationIdField'),
              value: integration.id,
              copyableText: this._labelsProviderPipe.transform('integrationsPage.copyIntegrationsId'),
              isCopyable: true,
            },
            {
              key: this._labelsProviderPipe.transform('integrationsPage.integrationMethodField'),
              value: Label.get(AuthenticationMethodToLabelPath[integration.authenticationMethod]),
            },
          ];

      const rightCardFields: ICardField[] = [
        {
          key: this._labelsProviderPipe.transform('integrationsPage.createdAtField'),
          value: formatFromDate(integration.createdAt),
        },
        {
          key: this._labelsProviderPipe.transform('integrationsPage.createdByField'),
          value: integration.createdBy,
        },
        {
          key: this._labelsProviderPipe.transform('integrationsPage.lastChangedField'),
          value: formatFromDate(integration.lastUpdated),
        },
        {
          key: this._labelsProviderPipe.transform('integrationsPage.lastChangedByField'),
          value: integration.lastUpdatedBy,
        },
      ];

      const integrationCard: IInfoCard = {
        cardId: integration.id,
        primaryCardTitle: integration.name,
        secondaryCardTitle: this._labelsProviderPipe.transform('integrationsPage.lastScan'),
        secondaryCardValue: this._displayValueForLastScan(integration.lastScanEndedAt),
        secondaryCardTooltipText: this._displayValueForDataCollection(integration.dataCollectionStartedAt),
        statusLabel: getIntegrationStatusLabel(integration.connectionStatus),
        statusTooltip: integration.connectionStatus.errorDisplayMessage,
        leftCardFields: leftCardFields,
        rightCardFields: rightCardFields,
        menuActions: this._getActionsMenu(integration.connectionStatus),
        internalNavigation: {
          query: { integration: integration.id },
        } as InternalNavigationTriggerModel,
        hasInlineWebhook: hasInlineWebhook,
        platformLogoPath: PlatformIconPaths[integration.platform],
        cardType: 'integration',
      };

      cards.push(integrationCard);
    });

    return cards;
  }

  private _displayValueForLastScan(lastScanEndedAt: string | null | undefined): string {
    switch (lastScanEndedAt) {
      case undefined:
        return this._labelsProviderPipe.transform('integrationsPage.scanDataNotFound');
      case null:
        return this._labelsProviderPipe.transform('integrationsPage.pending1stScanResults');
      default:
        return formatFromDateWithTimezone(new Date(lastScanEndedAt));
    }
  }

  private _displayValueForDataCollection(dataCollectionStartedAt: string | null | undefined): string {
    switch (dataCollectionStartedAt) {
      case undefined:
        return this._labelsProviderPipe.transform('integrationsPage.errorFetchingScanFreshnessData');
      case null:
        return '';
      default:
        return `${this._labelsProviderPipe.transform('integrationsPage.startedCollectingAt')}: ${formatFromDate(new Date(dataCollectionStartedAt))}`;
    }
  }

  private _getIntegrationActions(integrationStatus: IntegrationStatusModel): IntegrationActionsEnum[] {
    const actions = [];

    if (integrationStatus.code === StatusCodeEnum.off && integrationStatus.reason === StatusReasonEnum.deactivated) {
      actions.push(IntegrationActionsEnum.enable);
    }

    if (integrationStatus.code === StatusCodeEnum.on) {
      actions.push(IntegrationActionsEnum.disable);
    }

    actions.push(IntegrationActionsEnum.edit);

    if (integrationStatus.code === StatusCodeEnum.off && integrationStatus.reason === StatusReasonEnum.failing) {
      actions.push(IntegrationActionsEnum.retry);
    }

    actions.push(IntegrationActionsEnum.delete);
    return actions;
  }

  private _canEdit(): boolean {
    return this.permissionsService.isAdmin();
  }
}
